import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { reviewsData } from '../data/menuData';

const ReviewsSection = () => {
  const { language, t } = useLanguage();
  const scrollRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  // Duplicate reviews for infinite scroll effect
  const duplicatedReviews = [...reviewsData, ...reviewsData];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || isHovered) return;

    let animationId;
    let scrollPosition = 0;
    const scrollSpeed = 0.5;

    const animate = () => {
      scrollPosition += scrollSpeed;
      if (scrollPosition >= scrollContainer.scrollWidth / 2) {
        scrollPosition = 0;
      }
      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isHovered]);

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'Uber Eats': return 'bg-[#06C167]';
      case 'Lieferando': return 'bg-[#FF8000]';
      case 'Wolt': return 'bg-[#009DE0]';
      case 'Google': return 'bg-[#4285F4]';
      case 'Business': return 'bg-hanse-navy';
      default: return 'bg-gray-500';
    }
  };

  return (
    <section 
      id="reviews" 
      className="py-16 md:py-24 bg-hanse-navy"
      data-testid="reviews-section"
    >
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            ⭐ {t('reviews.title')}
          </h2>
        </motion.div>

        {/* Reviews Carousel */}
        <div
          ref={scrollRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="flex gap-6 overflow-x-auto hide-scrollbar pb-4"
          style={{ scrollBehavior: isHovered ? 'smooth' : 'auto' }}
        >
          {duplicatedReviews.map((review, index) => (
            <motion.div
              key={`${review.id}-${index}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: Math.min(index * 0.1, 0.3) }}
              className="flex-shrink-0 w-80 bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10"
              data-testid={`review-card-${review.id}-${index}`}
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-hanse-gold flex items-center justify-center text-hanse-dark font-bold">
                  {review.initials}
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold">{review.name}</h4>
                  <span className={`${getPlatformColor(review.platform)} text-white text-xs px-2 py-0.5 rounded-full`}>
                    {review.platform}
                  </span>
                </div>
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-3">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-hanse-gold fill-hanse-gold" />
                ))}
              </div>

              {/* Text */}
              <p className="text-white/80 text-sm leading-relaxed">
                "{language === 'de' ? review.text : review.textEn}"
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
